import type { ReactNode } from "react";
import type { TemplateRootSettingsKey, TemplateSettingsKey } from "@/pages/constructor-page/api";
import { FINSET_ICON_NAMES, renderFinsetIconOption } from "@/components/icons";

export const SettingFieldsTypes = {
  boolean: "boolean",
  string: "string",
  number: "number",
  color: "color",
  select: "select",
  array: "array",
  offerFields: "offerFields",
  tabs: "tabs",
  groups: "groups",
  hero: "hero",
  textarea: "textarea",
  image: "image",
  logo: "logo",
  pageFeatures: "pageFeatures",
  news: "news",
  newsShort: "newsShort",
  bgImages: "bgImages",
  comebacker: "comebacker",
  arrayShort: "arrayShort",
  arrayShortInt: "arrayShortInt",
} as const;

export type SettingsMetaOptionType = {
  id: string | number;
  name: string;
};

export type SettingFieldType = (typeof SettingFieldsTypes)[keyof typeof SettingFieldsTypes];

export const categoryOrder = [
  "common",
  "header",
  "hero",
  "appearance",
  "offers",
  "footer",
] as const;

export type SettingCategory = (typeof categoryOrder)[number];

export type TemplateImageType = string | string[] | { img: string; caption?: string }[];

export type TemplateImageMap = { _default: TemplateImageType } & Record<string, TemplateImageType>;

export type TemplateDescriptionMap = { _default: string } & Record<string, string>;

export type TemplateOptionsMap = { _default: SettingsMetaOptionType[] } & Record<
  string,
  SettingsMetaOptionType[]
>;

export const ObjectFieldColumnTypes = {
  string: "string",
  number: "number",
  boolean: "boolean",
  select: "select",
  object: "object",
} as const;

export type ObjectFieldColumnType =
  (typeof ObjectFieldColumnTypes)[keyof typeof ObjectFieldColumnTypes];

export type ObjectFieldColumn = {
  key: string;
  type: ObjectFieldColumnType;
  label?: string;
  placeholder?: string;
  options?: SettingsMetaOptionType[] | TemplateOptionsMap;
  renderOption?: (option: SettingsMetaOptionType) => ReactNode;
  fields?: ObjectFieldColumn[];
  metaKey?: string;
};

export type TemplateFieldsMap = { _default: ObjectFieldColumn[] } & Record<
  string,
  ObjectFieldColumn[]
>;

export type SettingMeta = {
  description?: string | TemplateDescriptionMap;
  type: SettingFieldType;
  category?: SettingCategory;
  label: string;
  image?: string | TemplateImageMap;
  options?: SettingsMetaOptionType[] | TemplateOptionsMap;
  placeholder?: string;
  defaultValue?: unknown;
  required?: boolean;
  fields?: ObjectFieldColumn[] | TemplateFieldsMap;
  maxItems?: number;
  key: TemplateRootSettingsKey | TemplateSettingsKey | ExtraDataMetaKeys;
};

type MetaRootMap = Partial<Record<TemplateRootSettingsKey, SettingMeta>>;

export const ROOT_SETTINGS_META: MetaRootMap = {
  pageTitle: {
    label: "Тайтл страницы",
    type: "string",
    category: "common",
    key: "pageTitle",
  },
  favicon: {
    label: "Фавикон",
    type: "image",
    category: "common",
    key: "favicon",
  },
  script: {
    label: "Скрипт",
    description: "JS-скрипт, который вставляется в страницу",
    type: "textarea",
    category: "common",
    key: "script",
  },
  comebacker: {
    label: "Камбэкер",
    description: "URL, на который перенаправит пользователя при нажатии кнопки «Назад» в браузере",
    type: "comebacker",
    category: "common",
    key: "comebacker",
  },
  yandexConsent: {
    label: "Разрешение для Яндекс Метрики",
    type: "boolean",
    key: "yandexConsent",
    description:
      "Пока пользователь не примет куки, код Яндекс Метрики не будет загружаться и не начнет собирать данные",
  },
  yandexIds: {
    label: "Идентификаторы Яндекс Метрики",
    key: "yandexIds",
    type: "arrayShortInt",
  },
  country: {
    label: "Страна",
    type: "select",
    category: "common",
    key: "country",
    options: [
      { id: 0, name: "Other" },
      { id: 1, name: "Ru" },
      { id: 2, name: "Kz" },
    ],
  },
};

type MetaMap = Partial<Record<TemplateSettingsKey, SettingMeta>>;

export const SETTINGS_META: MetaMap = {
  headerTextAlt: {
    label: "Альтернативный текст в заголовке",
    description: {
      _default: "Альтернативный текст в заголовке",
      purple:
        "Альтернативный текст в заголовке. Когда включено — отображает «Займы» и «в России» вместо «Займы онлайн» и «Одобрение за 5 минут».",
      qintor:
        "Альтернативный текст в заголовке. Когда включено — отображает «Займы на карту 24/7» вместо «Выгодные предложения онлайн 24/7».",
    },
    image: {
      _default: [
        { img: "/imgs/settings/qintor/headerTextAlt.png", caption: "qintor" },
        { img: "/imgs/settings/purple/purple__headerTextAlt.png", caption: "purple" },
      ],
      qintor: "/imgs/settings/qintor/headerTextAlt.png",
      purple: "/imgs/settings/purple/purple__headerTextAlt.png",
    },
    type: "boolean",
    category: "header",
    key: "headerTextAlt",
  },
  headerBenefitsVar: {
    label: "Вариант преимуществ в заголовке",
    description: "Вариант отображения преимуществ в заголовке",
    type: "select",
    options: [
      { id: 1, name: "var1 - Одобрение 96%" },
      { id: 2, name: "var2 - Без переплат" },
      { id: 3, name: "var3 - Быстрое оформление" },
    ],
    category: "header",
    image: {
      _default: [
        { img: "/imgs/settings/qintor/headerBenefitsVar1.png", caption: "var1" },
        { img: "/imgs/settings/qintor/headerBenefitsVar2.png", caption: "var2" },
        { img: "/imgs/settings/qintor/headerBenefitsVar3.png", caption: "var3" },
      ],
    },
    key: "headerBenefitsVar",
  },
  extraHeader: {
    label: "Доп. шапка",
    description: "Дополнительная шапка над витриной",
    type: "select",
    options: {
      _default: [],
      finset: [{ id: 1, name: "Шапка ruru.ru" }],
    },
    category: "header",
    key: "extraHeader",
  },
  withRandomOffer: {
    label: "Случайный оффер",
    description:
      "Показывает кнопку со случайным оффером. Укажите для оффера значение в поле {имя_шаблона}_random_offer_priority (целое, положительное число) — чем выше значение, тем выше вероятность выбора этого оффера. Офферы без значения или со значением равным нулю не участвуют в выборе.",
    type: "boolean",
    category: "header",
    image: {
      _default: "/imgs/settings/blue/blue__withRandomOffer.png",
      blue: "/imgs/settings/blue/blue__withRandomOffer.png",
      fzaim: "/imgs/settings/fzaim/fzaim__withRandomOffer.png",
      green: "/imgs/settings/green/green__withRandomOffer.png",
      purple: "/imgs/settings/purple/purple__withRandomOffer.png",
      "winhub-orange": "/imgs/settings/winhub-orange/winhub-orange_withRandomOffer.png",
    },
    key: "withRandomOffer",
  },
  imgTenge: {
    label: "Символ тенге",
    description: "Заменяет символы валюты RUB на Tenge на изображении в баннере",
    type: "boolean",
    category: "header",
    image: {
      _default: [
        "/imgs/settings/fzaim/fzaim__ImgTenge_ru.png",
        "/imgs/settings/fzaim/fzaim__ImgTenge.png",
      ],
      fzaim: [
        "/imgs/settings/fzaim/fzaim__ImgTenge_ru.png",
        "/imgs/settings/fzaim/fzaim__ImgTenge.png",
      ],
      "zaim-man": [
        "/imgs/settings/zaim-man/zaim-man__ImgTenge.png",
        "/imgs/settings/zaim-man/zaim-man__ImgTenge_ru.png",
      ],
    },
    key: "imgTenge",
  },
  withDisclaimer: {
    label: "Показывать дисклеймер",
    description:
      "Показывает выделенный текст над офферами: «Важно! Оставляйте заявки только в те компании, куда вы ранее не обращались.»",
    type: "boolean",
    category: "hero",
    image: "/imgs/settings/common/withDisclaimer.png",
    key: "withDisclaimer",
  },
  withWarning: {
    label: "Показывать предупреждение",
    description: "Показывает текст над офферами: «Оценивайте свои финансовые возможности и риски.»",
    type: "boolean",
    category: "hero",
    image: "/imgs/settings/common/withWarning.png",
    key: "withWarning",
  },
  withErid: {
    label: "Юридическая информация в тултипе",
    description:
      "Показывает иконку на карточке оффера с тултипом юридической информации (erid, ИНН, компания). Если отключено — иконка скрыта",
    type: "boolean",
    category: "offers",
    key: "withErid",
  },
  withVpnWarning: {
    label: "Показывать VPN-предупреждение",
    description: "Показывает VPN-баннер с текстом: «🛡️ Отключите VPN — выше вероятность одобрения»",
    type: "boolean",
    category: "hero",
    image: "/imgs/settings/common/withVpnWarning.png",
    key: "withVpnWarning",
  },
  hero: {
    label: "Тексты преимуществ в hero-блоке",
    description: "Задает тексты преимуществ в hero-блоке.",
    type: "hero",
    category: "hero",
    maxItems: 2,
    fields: [
      { key: "name", type: "string", label: "Заголовок" },
      { key: "description", type: "string", label: "Подпись" },
    ],
    key: "hero",
  },
  accentColor: {
    label: "Акцентный цвет",
    description: "Основной акцентный цвет элементов",
    type: "color",
    category: "appearance",
    image: {
      _default: [
        "/imgs/settings/green-round/green-round__accentColor1.png",
        "/imgs/settings/green-round/green-round__accentColor2.png",
      ],
    },
    key: "accentColor",
  },
  accentTextColor: {
    label: "Цвет текста на акценте",
    description: "Цвет текста на акцентных элементах",
    type: "color",
    category: "appearance",
    image: {
      _default: [
        "/imgs/settings/green-round/green-round__accentColor1.png",
        "/imgs/settings/green-round/green-round__accentColor2.png",
      ],
    },
    key: "accentTextColor",
  },
  colorScheme: {
    label: "Цветовая схема",
    description: {
      _default: "Переключение цветовой схемы шаблона",
      finset:
        "Переключение цветовой схемы шаблона и некоторых отображаемых элементов: yellow (по умолчанию), blue",
    },
    type: "select",
    category: "appearance",
    options: {
      _default: [
        { id: "red", name: "red" },
        { id: "orange", name: "orange" },
      ],
      finset: [
        { id: "yellow", name: "yellow" },
        { id: "blue", name: "blue" },
      ],
    },
    image: {
      _default: [
        "/imgs/settings/promoneylink/promoneylink__colorScheme_orange.png",
        "/imgs/settings/promoneylink/promoneylink__colorScheme_red.png",
      ],
      finset: ["/imgs/settings/finset/finset.png", "/imgs/settings/finset/finset__yellow.png"],
    },
    key: "colorScheme",
  },
  tabs: {
    label: "Вкладки офферов",
    description: {
      _default:
        "Задает вкладки для групп офферов. Каждая вкладка связывается с группой по индексу. От типа зависят отображаемые поля у оффера: credit — Процент, Период, Описание, Сумма; debit — Процент, Период, Описание; default — Процент, Возраст, Сумма",
      finset:
        "Задает вкладки для групп офферов. Каждая вкладка связывается с группой по индексу. Тип не влияет на отображение полей — карточка оффера везде одинаковая. «Слаг» — сегмент URL вкладки: при переключении подставляется в путь (без перезагрузки), при заходе на /{слаг} вкладка становится активной. Для прямого открытия путей слагов нужны маппинги (host, path) на этот же манифест",
      ok: "Задает вкладки для групп офферов. Каждая вкладка связывается с группой по индексу. «Поля» — набор колонок условий на карточках офферов этой вкладки (подпись + поле оффера; «Ширина» — фиксированная ширина колонки в пикселях, без нее колонки делят место поровну). «Кнопка» — текст кнопки на карточках офферов вкладки (по умолчанию — «Получить»). Если поля не заданы — Сумма до, Срок до, Ставка от. Описание оффера отображается под колонками: до двух строк, при переполнении раскрывается кнопкой со стрелкой",
    },
    type: "tabs",
    fields: {
      _default: [
        { key: "title", type: "string", label: "Заголовок" },
        {
          key: "type",
          type: "select",
          label: "Тип",
          options: [
            { id: "credit", name: "credit" },
            { id: "debit", name: "debit" },
            { id: "offer", name: "offer" },
          ],
        },
      ],
      finset: [
        { key: "title", type: "string", label: "Заголовок" },
        { key: "slug", type: "string", label: "Слаг" },
      ],
      ok: [
        { key: "title", type: "string", label: "Заголовок" },
        { key: "buttonGroup", type: "string", label: "Кнопка" },
        {
          key: "fields",
          type: "object",
          label: "Поля",
          metaKey: "offerFields",
          fields: [
            { key: "label", type: "string", label: "Название" },
            {
              key: "key",
              type: "select",
              label: "Поле",
              options: [
                { id: "age", name: "age" },
                { id: "amount", name: "amount" },
                { id: "percent", name: "percent" },
                { id: "period", name: "period" },
              ],
            },
            { key: "width", type: "number", label: "Ширина" },
          ],
        },
      ],
    },
    category: "offers",
    image: {
      _default: [
        "/imgs/settings/common/tabs1.png",
        "/imgs/settings/common/tabs2.png",
        "/imgs/settings/common/tabs3.png",
      ],
    },
    key: "tabs",
  },
  groups: {
    label: "Типы групп офферов",
    description:
      "Задает тип офферов для групп. Каждая запись связывается с группой по индексу. Тип betting отображает офферы группы через шаблон беттинг-оффера, common — через обычный. Если настройка не указана или для группы нет записи, используется тип common",
    type: "groups",
    fields: [
      {
        key: "type",
        type: "select",
        label: "Тип",
        options: [
          { id: "common", name: "common" },
          { id: "betting", name: "betting" },
        ],
      },
    ],
    category: "offers",
    image: {
      _default: [
        "/imgs/settings/blue/blue__groups_common.png",
        "/imgs/settings/blue/blue__groups_betting.png",
      ],
      blue: [
        "/imgs/settings/blue/blue__groups_common.png",
        "/imgs/settings/blue/blue__groups_betting.png",
      ],
    },
    key: "groups",
  },
  initiallyVisibleCount: {
    label: "Количество видимых офферов",
    description:
      "Количество изначально видимых офферов в каждой группе. Остальные скрыты и раскрываются по кнопке «Показать еще». По умолчанию — 8",
    type: "number",
    category: "offers",
    // defaultValue: 8,
    // required: true,
    image: "/imgs/settings/common/initiallyVisibleCount.png",
    key: "initiallyVisibleCount",
  },
  offerFields: {
    label: "Поля офферов",
    description: "Определяет набор полей, которые отображаются в карточке",
    type: "offerFields",
    fields: [
      { key: "label", type: "string", label: "Название" },
      {
        key: "key",
        type: "select",
        label: "Поле",
        options: [
          { id: "age", name: "age" },
          { id: "amount", name: "amount" },
          { id: "percent", name: "percent" },
          { id: "period", name: "period" },
          { id: "description", name: "description" },
          { id: "psk", name: "psk" },
        ],
      },
    ],
    category: "offers",
    image: {
      _default: [
        "/imgs/settings/blue/blue__offerFields1.png",
        "/imgs/settings/blue/blue__offerFields2.png",
      ],
      blue: [
        "/imgs/settings/blue/blue__offerFields1.png",
        "/imgs/settings/blue/blue__offerFields2.png",
      ],
    },
    key: "offerFields",
  },
  withAdvTooltip: {
    label: "Бейдж «Реклама»",
    description:
      "Показывает бейдж «Реклама» на карточке оффера с тултипом, содержащим юридическую информацию (erid, ИНН, Компания). Когда отключено — юридическая информация отображается текстом под кнопкой",
    type: "boolean",
    category: "offers",
    image: "/imgs/settings/sky/sky__withAdvTooltip.png",
    key: "withAdvTooltip",
  },
  highlightOfferFieldTitle: {
    label: "Выделение заголовка поля",
    description:
      "Управляет тем, какой текст в поле карточки оффера выделен. Когда false (по умолчанию) — выделено значение поля. Когда true — выделен заголовок поля",
    type: "boolean",
    category: "offers",
    image: {
      _default: [
        "/imgs/settings/purple/purple__highlightOfferFieldTitle1.png",
        "/imgs/settings/purple/purple__highlightOfferFieldTitle2.png",
      ],
    },
    key: "highlightOfferFieldTitle",
  },
  showOfferLegalInfo: {
    label: "Юридическая информация оффера",
    description:
      "Показывает юридическую информацию на карточке оффера (erid, ИНН, Компания). Когда отключено — информация скрыта",
    type: "boolean",
    category: "offers",
    image: "/imgs/settings/common/showOfferLegalInfo.png",
    key: "showOfferLegalInfo",
  },
  footerCopyrightText: {
    label: "Текст копирайта",
    description:
      "Настраиваемый текст copyright в футере. Поддерживает плейсхолдеры {{year}} и {{host}}",
    type: "string",
    category: "footer",
    image: "/imgs/settings/common/footerCopyrightText.png",
    placeholder: "© {{year}} {{host}}",
    key: "footerCopyrightText",
  },
  footerDetails: {
    label: "Доп. текст в футере",
    description: "Дополнительный текст в футере",
    type: "array",
    category: "footer",
    image: "/imgs/settings/common/footerDetails.png",
    key: "footerDetails",
  },
  personalPolicy: {
    label: "Политика обработки персональных данных",
    description: "Включает отображение ссылки «Политика обработки персональных данных»",
    type: "boolean",
    category: "footer",
    image: {
      _default: "/imgs/settings/blue/blue__policyDocLink.png",
      blue: "/imgs/settings/blue/blue__policyDocLink.png",
      fzaim: "/imgs/settings/fzaim/fzaim__policyDocLink.png",
      green: "/imgs/settings/green/green__policyDocLink.png",
      "green-es": "/imgs/settings/green/green__policyDocLink.png",
      "green-lk": "/imgs/settings/green/green__policyDocLink.png",
    },
    key: "personalPolicy",
  },
  personalConsent: {
    label: "Согласие на обработку персональных данных",
    description: "Включает отображение ссылки «Согласие на обработку персональных данных»",
    type: "boolean",
    category: "footer",
    image: {
      _default: "/imgs/settings/blue/blue__policyDocLink.png",
      blue: "/imgs/settings/blue/blue__policyDocLink.png",
      fzaim: "/imgs/settings/fzaim/fzaim__policyDocLink.png",
      green: "/imgs/settings/green/green__policyDocLink.png",
      "green-es": "/imgs/settings/green/green__policyDocLink.png",
      "green-lk": "/imgs/settings/green/green__policyDocLink.png",
    },
    key: "personalConsent",
  },
  mailingConsent: {
    label: "Согласие на рассылку",
    description:
      "Включает отображение ссылки «Согласие на рассылку» (ссылка /g/consent/mailing_consent) в футере",
    type: "boolean",
    category: "footer",
    image: {
      _default: "/imgs/settings/blue/blue__policyDocLink.png",
      blue: "/imgs/settings/blue/blue__policyDocLink.png",
      fzaim: "/imgs/settings/fzaim/fzaim__policyDocLink.png",
      green: "/imgs/settings/green/green__policyDocLink.png",
      "green-es": "/imgs/settings/green/green__policyDocLink.png",
      "green-lk": "/imgs/settings/green/green__policyDocLink.png",
    },
    key: "mailingConsent",
  },
  cookiePopup: {
    label: "Куки-уведомление",
    description: "Показывает уведомление о куки внизу страницы с кнопкой Принять",
    type: "boolean",
    category: "footer",
    image: "/imgs/settings/green/green__cookiePopup.png",
    key: "cookiePopup",
  },
  withRegister: {
    label: "Форма регистрации",
    description: "Показывает кнопку «Зарегистрироваться» и модальное окно регистрации",
    type: "boolean",
    category: "footer",
    image: {
      _default: [
        "/imgs/settings/blue/blue__policyDocLink.png",
        "/imgs/settings/blue/blue__withRegister.png",
      ],
      blue: [
        "/imgs/settings/blue/blue__policyDocLink.png",
        "/imgs/settings/blue/blue__withRegister.png",
      ],
    },
    key: "withRegister",
  },
  withUnsub: {
    label: "Форма отписки",
    description: "Показывает кнопку «Отписаться» и модальное окно отписки",
    type: "boolean",
    category: "footer",
    image: {
      _default: [
        "/imgs/settings/fzaim/fzaim__withUnsub.png",
        "/imgs/settings/fzaim/fzaim__withUnsub1.png",
      ],
      fzaim: [
        "/imgs/settings/fzaim/fzaim__withUnsub.png",
        "/imgs/settings/fzaim/fzaim__withUnsub1.png",
      ],
      green: "/imgs/settings/green/green__withUnsub.png",
    },
    key: "withUnsub",
  },
  pageType: {
    label: "Тип страницы",
    description: "Определяет заголовок и структуру страницы",
    type: "select",
    category: "appearance",
    image: {
      _default: ["/imgs/settings/yota/yota_pageType_credits.png"],
      yota: [
        { img: "/imgs/settings/yota/yota_pageType_credits.png", caption: "credits" },
        { img: "/imgs/settings/yota/yota_pageType_creditCards.png", caption: "creditCards" },
        { img: "/imgs/settings/yota/yota_pageType_debitCards.png", caption: "debitCards" },
        { img: "/imgs/settings/yota/yota_pageType_microloans.png", caption: "microloans" },
        { img: "/imgs/settings/yota/yota.png", caption: "main" },
      ],
      megafon: [
        { img: "/imgs/settings/megafon/megafon_pageType_credits.png", caption: "credits" },
        {
          img: "/imgs/settings/megafon/megafon_pageType_creditCards.png",
          caption: "creditCards",
        },
        { img: "/imgs/settings/megafon/megafon_pageType_debitCards.png", caption: "debitCards" },
        { img: "/imgs/settings/megafon/megafon_pageType_microloans.png", caption: "microloans" },
      ],
      "r-gamble": [
        { img: "/imgs/settings/r-gamble/r-gamble_pageType_main.png", caption: "main" },
        {
          img: "/imgs/settings/r-gamble/r-gamble_pageType_vpn.png",
          caption: "vpn",
        },
      ],
    },
    options: {
      _default: [{ id: "main", name: "main" }],
      megafon: [
        { id: "microloans", name: "microloans" },
        { id: "debitCards", name: "debitCards" },
        { id: "creditCards", name: "creditCards" },
        { id: "credits", name: "credits" },
      ],
      yota: [
        { id: "main", name: "main" },
        { id: "microloans", name: "microloans" },
        { id: "debitCards", name: "debitCards" },
        { id: "creditCards", name: "creditCards" },
        { id: "credits", name: "credits" },
      ],
      "r-gamble": [
        { id: "main", name: "main" },
        { id: "vpn", name: "vpn" },
      ],
    },
    key: "pageType",
  },
  pageFeatures: {
    label: "Промо-блоки",
    description: "Промо-карточки с ссылками на другие страницы",
    type: "pageFeatures",
    category: "appearance",
    key: "pageFeatures",
    fields: [
      { key: "link", type: "string", label: "Ссылка" },
      {
        key: "type",
        type: "select",
        label: "Тип",
        options: [
          { id: "creditCards", name: "creditCards" },
          { id: "debitCards", name: "debitCards" },
          { id: "microloans", name: "microloans" },
        ],
      },
    ],

    image: { _default: "/imgs/settings/yota/yota_pageFeatures.png" },
  },
  additionalLink: {
    label: "Дополнительная ссылка",
    description: 'Ссылка "Все предложения"',
    type: "string",
    category: "appearance",
    key: "additionalLink",
    image: { _default: "/imgs/settings/yota/yota_additionalLink.png" },
  },
  footerVariant: {
    label: "Вариант футера",
    description: "Вариант текста в футере",
    type: "select",
    category: "footer",
    options: [
      { id: 1, name: "Стандартный (МФО)" },
      { id: 2, name: "Информационный" },
    ],
    key: "footerVariant",
    image: {
      _default: ["/imgs/settings/yota/yota_footerVariant_1.png"],
      yota: [
        { img: "/imgs/settings/yota/yota_footerVariant_1.png", caption: "1" },
        { img: "/imgs/settings/yota/yota_footerVariant_2.png", caption: "2" },
      ],
      megafon: [
        { img: "/imgs/settings/megafon/megafon_footerVariant_1.png", caption: "1" },
        {
          img: "/imgs/settings/megafon/megafon_footerVariant_2.png",
          caption: "2",
        },
      ],
    },
  },
  news: {
    label: "Новости",
    description: "Карточки новостей с изображением, заголовком, описанием, значками и датой.",
    type: "news",
    category: "appearance",
    key: "news",
    image: {
      _default: "/imgs/settings/winhub/winhub_news.png",
      "winhub-orange": "/imgs/settings/winhub-orange/winhub-orange_news.png",
    },
  },
  newsShort: {
    label: "События",
    description: "Карточки с заголовком, описанием и числом.",
    type: "newsShort",
    category: "appearance",
    key: "newsShort",
    image: { _default: "/imgs/settings/winhub-orange/winhub-orange_news-short.png" },
  },
  withLegalFooter: {
    label: "Показывать блок с юридической информацией",
    description: "Показывает блок с юридической информацией",
    type: "boolean",
    category: "footer",
    image: { _default: "/imgs/settings/winhub/winhub__withLegalFooter.png" },
    key: "withLegalFooter",
  },
  headerTitle: {
    label: "Текст в заголовке страницы",
    description: "Показывает текст в заголовке страницы",
    type: "string",
    category: "header",
    image: { _default: "/imgs/settings/winhub/winhub__headerTitle.png" },
    key: "headerTitle",
  },
  logo: {
    label: "Логотип",
    description:
      "Логотип сайта в шапке и футере. Можно выбрать из загруженных или загрузить новый. Если не задан — используется логотип шаблона по умолчанию",
    type: "logo",
    category: "header",
    key: "logo",
  },
};

const FINSET_ICON_OPTIONS: SettingsMetaOptionType[] = FINSET_ICON_NAMES.map((name) => ({
  id: name,
  name,
}));

export const EXTRA_DATA_META: Record<ExtraDataMetaKeys, Omit<SettingMeta, "type">> = {
  koshelekpro_approval: {
    key: "koshelekpro_approval",
    label: "koshelekpro_approval",
    description:
      "Уровень одобрения, отображаемый на карточке оффера. Влияет на визуальный стиль: «среднее» — оранжевый цвет текста, «очень» — красный, «высокое» — зеленый. Пример: «Очень высокое» - будет отображен текст «Очень высокое» красного цвета",
    image: {
      _default: [
        {
          img: "/imgs/settings/extraData/koshelekpro/koshelekpro_approval1.png",
          caption: "Среднее",
        },
        {
          img: "/imgs/settings/extraData/koshelekpro/koshelekpro_approval2.png",
          caption: "Высокое",
        },
        {
          img: "/imgs/settings/extraData/koshelekpro/koshelekpro_approval3.png",
          caption: "Очень",
        },
      ],
    },
  },
  magnit_badges: {
    key: "magnit_badges",
    label: "magnit_badges",
    description:
      "Специальные бейджи на карточке оффера, указываются через запятую. Возможные значения: «new» — бейдж «Новинка», «bonus» — бейдж «+30», «bonus:n» — бейдж «+n» с числом (без указания числа будет показано число 30). Пример: «new,bonus:50» - будет отображен бейдж «Новинка» и бейдж «+50»",
    image: {
      _default: [
        {
          img: "/imgs/settings/extraData/magnit/magnit_badges1.png",
          caption: "'new,bonus:100'",
        },
        {
          img: "/imgs/settings/extraData/magnit/magnit_badges2.png",
          caption: "'new,bonus'",
        },
        {
          img: "/imgs/settings/extraData/magnit/magnit_badges3.png",
          caption: "'bonus:75",
        },
      ],
    },
  },
  max_badge: {
    key: "max_badge",
    label: "max_badge",
    description:
      "Бейдж в правом верхнем углу карточки оффера (например, «Хит» или «Без справок»). Если пусто — бейдж скрыт.",
  },
  mint_docs: {
    key: "mint_docs",
    label: "mint_docs",
    description:
      "URL-ссылка на документацию оффера. Отображается как кнопка «Информация». Если пусто — кнопка скрыта.",
    image: {
      _default: [
        {
          img: "/imgs/settings/extraData/mint/mint_docs.png",
        },
        {
          img: "/imgs/settings/extraData/mint/mint_docs2.png",
        },
      ],
    },
  },
  r_gamble_usersCount: {
    key: "r_gamble_usersCount",
    label: "r_gamble_usersCount",
    description:
      "Количество пользователей, выводящееся под названием оффера в формате: «Выбрали: N пользователей»",
    image: {
      _default: [
        {
          img: "/imgs/settings/extraData/r_gamble_usersCount.png",
        },
      ],
    },
  },
  ok_badge: {
    key: "ok_badge",
    label: "ok_badge",
    description: "Бейдж на карточке оффера (например, «Новинка»). Если пусто — бейдж скрыт.",
  },
  r_betting_rating: {
    key: "r_betting_rating",
    label: "r_betting_rating",
    description: "Рейтинг оффера в виде количества заполненных звёзд (0–5). Целое число.",
    image: {
      _default: [
        {
          img: "/imgs/settings/extraData/r_betting_rating.png",
        },
      ],
    },
  },
  winhit_rating: {
    key: "winhit_rating",
    label: "winhit_rating",
    description:
      "Рейтинг оффера (0–5). Поддерживает дробные значения (например, 4.5). Отображается как звёзды и числовое значение.",
    image: {
      _default: [
        {
          img: "/imgs/settings/extraData/winhit_rating.png",
        },
      ],
    },
  },
  winhit_badges: {
    key: "winhit_badges",
    label: "winhit_badges",
    description:
      "Список бейджей через запятую. Если пусто, отображаются значения по умолчанию: «Широкая линейка, Быстрые выплаты, Мобильное приложение».",
    image: {
      _default: [
        {
          img: "/imgs/settings/extraData/winhit_badges.png",
        },
      ],
    },
  },
  winhub_rating: {
    key: "winhub_rating",
    label: "winhub_rating",
    description:
      "Рейтинг оффера (0–5). Поддерживает дробные значения (например, 4.5). Отображается как звёзды и числовое значение.",
    image: {
      _default: [
        {
          img: "/imgs/settings/extraData/winhub_rating.png",
        },
      ],
    },
  },
  winhub_badges: {
    key: "winhub_badges",
    label: "winhub_badges",
    description:
      "Список бейджей через запятую. Если пусто, отображаются значения по умолчанию: «Широкая линейка», «Быстрые выплаты», «Мобильное приложение».",
    image: {
      _default: [
        {
          img: "/imgs/settings/extraData/winhub_badges.png",
        },
      ],
    },
  },
  blue_random_offer_priority: {
    key: "blue_random_offer_priority",
    label: "blue_random_offer_priority",
    description:
      "Вес для выбора случайного оффера. Работает при включённой настройке «Случайный оффер».",
  },
  finset_metrics: {
    key: "finset_metrics",
    label: "finset_metrics",
    description: "Метрики карточки оффера. Не более 3 элементов",
    maxItems: 3,
    fields: [
      { key: "value", type: "string", label: "Значение" },
      { key: "label", type: "string", label: "Подпись" },
    ],
  },
  finset_advantages: {
    key: "finset_advantages",
    label: "finset_advantages",
    description: "Преимущества модалки «Подробнее». Не более 7 элементов",
    maxItems: 7,
    fields: [
      { key: "title", type: "string", label: "Заголовок" },
      { key: "subtitle", type: "string", label: "Описание" },
      {
        key: "icon",
        type: "select",
        label: "Иконка",
        options: FINSET_ICON_OPTIONS,
        renderOption: renderFinsetIconOption,
      },
    ],
  },
  finset_adv_extra: {
    key: "finset_adv_extra",
    label: "finset_adv_extra",
    description:
      "Дополнительная строка в тултипе маркера рекламодателя (кружок «i» в углу карточки)",
  },
  fzaim_random_offer_priority: {
    key: "fzaim_random_offer_priority",
    label: "fzaim_random_offer_priority",
    description:
      "Вес для выбора случайного оффера. Работает при включённой настройке «Случайный оффер».",
  },
  green_random_offer_priority: {
    key: "green_random_offer_priority",
    label: "green_random_offer_priority",
    description:
      "Вес для выбора случайного оффера. Работает при включённой настройке «Случайный оффер».",
  },
  green_es_random_offer_priority: {
    key: "green_es_random_offer_priority",
    label: "green_es_random_offer_priority",
    description:
      "Вес для выбора случайного оффера. Работает при включённой настройке «Случайный оффер».",
  },
  green_lk_random_offer_priority: {
    key: "green_lk_random_offer_priority",
    label: "green_lk_random_offer_priority",
    description:
      "Вес для выбора случайного оффера. Работает при включённой настройке «Случайный оффер».",
  },
  purple_random_offer_priority: {
    key: "purple_random_offer_priority",
    label: "purple_random_offer_priority",
    description:
      "Вес для выбора случайного оффера. Работает при включённой настройке «Случайный оффер».",
  },
  winhub_orange_random_offer_priority: {
    key: "winhub_orange_random_offer_priority",
    label: "winhub_orange_random_offer_priority",
    description:
      "Вес для выбора случайного оффера. Работает при включённой настройке «Случайный оффер».",
  },
  winhub_orange_badges: {
    key: "winhub_orange_badges",
    label: "winhub_orange_badges",
    description: "Преимущества на баннерах, которые располагаются между новостями",
    image: {
      _default: [
        {
          img: "/imgs/settings/extraData/winhub-orange/winhub-orange_badges.png",
        },
      ],
    },
  },
};

export type ExtraDataMeta = {
  koshelekpro_approval: string;
  magnit_badges: string;
  max_badge: string;
  mint_docs: string;
  ok_badge: string;
  r_gamble_usersCount: string;
  r_betting_rating: string;
  winhit_rating: string;
  winhit_badges: string;
  winhub_rating: string;
  winhub_badges: string;
  blue_random_offer_priority: string;
  finset_metrics: string;
  finset_advantages: string;
  finset_adv_extra: string;
  fzaim_random_offer_priority: string;
  green_random_offer_priority: string;
  green_es_random_offer_priority: string;
  green_lk_random_offer_priority: string;
  purple_random_offer_priority: string;
  winhub_orange_random_offer_priority: string;
  winhub_orange_badges: string;
};

export type ExtraDataMetaKeys = keyof ExtraDataMeta;
// const SETTINGS_ORDER: TemplateSettingsKey[] = Object.keys(SETTINGS_META) as TemplateSettingsKey[];

export const TEMPLATE_SETTINGS: Record<string, TemplateSettingsKey[]> = {
  black: [
    "withDisclaimer",
    "withWarning",
    "withVpnWarning",
    "footerCopyrightText",
    "footerDetails",
    "offerFields",
  ],
  blue: [
    "withDisclaimer",
    "withWarning",
    "withVpnWarning",
    "withRandomOffer",
    "logo",
    "offerFields",
    "groups",
    "personalPolicy",
    "personalConsent",
    "mailingConsent",
    "withRegister",
    "footerCopyrightText",
    "footerDetails",
  ],
  dengirf: ["footerCopyrightText", "tabs"],
  findcredit: ["footerCopyrightText", "tabs"],
  finset: [
    "extraHeader",
    "withVpnWarning",
    "colorScheme",
    "tabs",
    "footerCopyrightText",
    "footerDetails",
  ],
  fzaim: [
    "withDisclaimer",
    "imgTenge",
    "personalPolicy",
    "personalConsent",
    "withUnsub",
    "withRegister",
    "withRandomOffer",
  ],
  green: [
    "withDisclaimer",
    "withWarning",
    "withVpnWarning",
    "cookiePopup",
    "personalPolicy",
    "personalConsent",
    "mailingConsent",
    "withUnsub",
    "withRegister",
    "offerFields",
    "withRandomOffer",
    "logo",
    "footerCopyrightText",
    "footerDetails",
  ],
  "green-round": [
    "withDisclaimer",
    "withWarning",
    "logo",
    "footerCopyrightText",
    "accentColor",
    "accentTextColor",
  ],
  "green-es": [
    "withDisclaimer",
    "withWarning",
    "cookiePopup",
    "personalPolicy",
    "personalConsent",
    "withRandomOffer",
    "footerCopyrightText",
    "footerDetails",
  ],
  "green-lk": [
    "withDisclaimer",
    "withWarning",
    "cookiePopup",
    "personalPolicy",
    "personalConsent",
    "withRandomOffer",
    "footerCopyrightText",
    "footerDetails",
  ],
  koshelekpro: [],
  loanspace: ["logo", "footerCopyrightText"],
  magnit: [],
  max: ["withWarning", "withErid", "headerTitle", "footerCopyrightText", "footerDetails"],
  megafon: ["footerVariant", "pageType"],
  mint: ["footerCopyrightText", "footerDetails"],
  ok: ["withWarning", "hero", "tabs"],
  "online-zaymi": ["footerCopyrightText"],
  promoneylink: ["footerCopyrightText", "colorScheme"],
  purple: [
    "withDisclaimer",
    "withWarning",
    "withVpnWarning",
    "footerCopyrightText",
    "footerDetails",
    "headerTextAlt",
    "logo",
    "showOfferLegalInfo",
    "highlightOfferFieldTitle",
    "initiallyVisibleCount",
    "offerFields",
    "withRandomOffer",
  ],
  qintor: [
    "footerCopyrightText",
    "headerTextAlt",
    "headerBenefitsVar",
    "footerDetails",
    "offerFields",
    "withVpnWarning",
  ],
  "simple-color": ["withWarning"],
  simple: ["withWarning"],
  sky: ["logo", "footerCopyrightText", "withAdvTooltip"],
  "r-gamble": ["footerCopyrightText", "pageType"],
  "r-betting": ["withVpnWarning"],
  "table-chart": ["withWarning"],
  vozmykredit: ["tabs"],
  winhub: ["footerDetails", "footerCopyrightText", "news", "headerTitle", "withLegalFooter"],
  winhit: ["footerCopyrightText", "headerTitle"],
  yota: ["additionalLink", "footerVariant", "pageFeatures", "pageType"],
  "zaim-man": [
    "withDisclaimer",
    "withWarning",
    "withVpnWarning",
    "footerCopyrightText",
    "imgTenge",
    "logo",
    "footerDetails",
    "showOfferLegalInfo",
    "initiallyVisibleCount",
  ],
  "winhub-orange": [
    "withRandomOffer",
    "footerCopyrightText",
    "news",
    "newsShort",
    "withLegalFooter",
  ],
  forest: ["withWarning"],

  // Test / debug template — has every possible setting
  // _test: SETTINGS_ORDER,
};

export const SLOTS_TEMPLATE_LIST = ["blue", "green", "magnit", "winhub-orange"];
export function supportSlots(templateName: string) {
  return SLOTS_TEMPLATE_LIST.includes(templateName);
}

export function getSettingsKeysForTemplate(templateName: string) {
  return TEMPLATE_SETTINGS[templateName] ?? [];
}

export function getTemplatesForSettingKey(settingKey: string) {
  return Object.entries(TEMPLATE_SETTINGS)
    .filter(([, keys]) => keys.includes(settingKey as TemplateSettingsKey))
    .map(([templateName]) => templateName);
}
